/** 商家设计工作室：锁定能力 draft / context（对齐规格 §7） */

import type { CapabilityAccess, CapabilityDraft, MaterialsBinding } from "@/lib/hermes-capability-bindings";
import {
  persistSkillsForDraft,
  setBrandToolAccess,
  setKbWikiAccess,
  setMaterialsAccess,
} from "@/lib/hermes-capability-bindings";
import type { KbWikiBinding } from "@/lib/hermes-api";
import type { BrandSiteAuthoringContext } from "@/lib/hermes-brand-site-authoring";
import { brandSiteAuthoringChatContext } from "@/lib/hermes-brand-site-authoring";

export type BrandSiteStudioLockInput = {
  brandId: number;
  brandSiteId: number;
  enterpriseWikiId?: number | null;
  /** 有品牌 Wiki 时至少只读；已有 write 则保留 */
  kbBindings?: KbWikiBinding[];
  /** 默认只读；可升 write */
  materialsAccess?: "read" | "write";
  /** 额外 skills（会去掉 no_fetch） */
  skills?: string[];
};

export type BrandSiteStudioPanelLock = {
  brandId: number;
  floor: CapabilityDraft;
};

function maxAccess(current: CapabilityAccess | null, floor: CapabilityAccess): CapabilityAccess {
  if (current === "write" || floor === "write") return "write";
  return "read";
}

function materialsRowAccess(
  draft: CapabilityDraft,
  target: { scope: "enterprise" } | { scope: "brand"; brand_id: number },
): CapabilityAccess | null {
  const row = draft.materials.find((item) =>
    target.scope === "enterprise"
      ? item.scope === "enterprise"
      : item.scope === "brand" && item.brand_id === target.brand_id,
  );
  return row?.access ?? null;
}

function kbRowAccess(draft: CapabilityDraft, wikiId: number): CapabilityAccess | null {
  return draft.kbBindings.find((row) => row.wiki_id === wikiId)?.access ?? null;
}

/** 工作室必选绑定（前端写入 create/update；服务端仍会再合并）。 */
export function buildBrandSiteStudioLockedDraft(input: BrandSiteStudioLockInput): CapabilityDraft {
  const materialsAccess = input.materialsAccess === "write" ? "write" : "read";
  const materials: MaterialsBinding[] = [
    { scope: "enterprise", access: "read" },
    { scope: "brand", brand_id: input.brandId, access: materialsAccess },
  ];

  const kbBindings: KbWikiBinding[] = [];
  if (input.enterpriseWikiId != null && input.enterpriseWikiId > 0) {
    kbBindings.push({ wiki_id: input.enterpriseWikiId, access: "read" });
  }
  for (const row of input.kbBindings ?? []) {
    const access = row.access === "write" ? ("write" as const) : ("read" as const);
    const exists = kbBindings.some((item) => item.wiki_id === row.wiki_id);
    if (!exists) kbBindings.push({ wiki_id: row.wiki_id, access });
    else {
      const idx = kbBindings.findIndex((item) => item.wiki_id === row.wiki_id);
      kbBindings[idx] = {
        wiki_id: row.wiki_id,
        access: maxAccess(kbBindings[idx]?.access ?? null, access),
      };
    }
  }

  return {
    fetchOn: true,
    materials,
    kbBindings,
    journey: [],
    brandSite: [{ brand_id: input.brandId, access: "write" }],
  };
}

/** 用户改 draft 后合并必选底限，保留额外只读加码。 */
export function mergeBrandSiteStudioFloor(user: CapabilityDraft, floor: CapabilityDraft): CapabilityDraft {
  let merged: CapabilityDraft = { ...user, fetchOn: true };

  for (const row of floor.materials) {
    const target =
      row.scope === "enterprise"
        ? ({ scope: "enterprise" } as const)
        : ({ scope: "brand", brand_id: row.brand_id } as const);
    const access = maxAccess(materialsRowAccess(merged, target), row.access);
    merged = setMaterialsAccess(merged, target, access);
  }

  for (const row of floor.kbBindings) {
    const access = maxAccess(kbRowAccess(merged, row.wiki_id), row.access);
    merged = setKbWikiAccess(merged, row.wiki_id, access);
  }

  for (const row of floor.brandSite) {
    if (row.access === "write") {
      merged = setBrandToolAccess(merged, "brandSite", row.brand_id, "write");
    }
  }

  return merged;
}

export function buildBrandSiteStudioChatContext(
  ctx: BrandSiteAuthoringContext,
  lock: Omit<BrandSiteStudioLockInput, "brandId" | "brandSiteId"> = {},
) {
  const draft = buildBrandSiteStudioLockedDraft({
    brandId: ctx.brandId,
    brandSiteId: ctx.brandSiteId,
    ...lock,
  });
  return {
    ...draftToContextFields(draft),
    ...brandSiteAuthoringChatContext(ctx),
  };
}

function draftToContextFields(draft: CapabilityDraft) {
  return {
    kb_bindings: draft.kbBindings,
    materials_bindings: draft.materials,
    journey_bindings: draft.journey,
    brand_site_bindings: draft.brandSite,
  };
}

export function brandSiteStudioSkills(draft: CapabilityDraft, skills: string[] = []): string[] {
  return persistSkillsForDraft(draft, skills).filter((s) => s !== "no_fetch");
}
